import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Snowflake, User, Building2, Check, Mail, ArrowRight, FileText } from "lucide-react";
import { PageHeader } from "@/components/site/PageHeader";
import { Seo } from "@/components/site/Seo";

const BULLETS = [
  "Corso di preparazione ed esame teorico-pratico",
  "Certificazione per le persone e per le imprese",
  "Pratiche e iscrizione al Registro telematico nazionale FGAS",
  "Aggiornamenti e rinnovi alla scadenza",
];

const PATHS = [
  {
    icon: User,
    title: "Per le persone",
    body: "Tecnici e frigoristi: abilitazione individuale per installare, manutenere, riparare o smaltire apparecchiature con gas fluorurati, con corso ed esame in un'unica sede.",
  },
  {
    icon: Building2,
    title: "Per le imprese",
    body: "Aziende della refrigerazione e della climatizzazione: certificazione aziendale obbligatoria per operare sugli impianti, con supporto documentale completo.",
  },
];

const STEPS = [
  "Ci racconti se ti serve la certificazione come persona o come impresa.",
  "Ti prepari con noi: corso mirato all'esame teorico-pratico.",
  "Sostieni l'esame teorico-pratico per la certificazione.",
  "Ottieni la certificazione e l'iscrizione al Registro telematico nazionale FGAS.",
];

export default function FgasPage() {
  return (
    <div data-testid="fgas-page">
      <Seo
        title="Certificazione FGAS a Frosinone — persone e imprese | treffe"
        description="Certificazione FGAS a Frosinone per persone e imprese: corso di preparazione, esame teorico-pratico e iscrizione al Registro telematico nazionale FGAS. Gas fluorurati, Reg. (UE) 2024/573."
        path="/fgas"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "Cos'è la certificazione FGAS?",
              "acceptedAnswer": { "@type": "Answer", "text": "È la certificazione obbligatoria per chi installa, mantiene, ripara o smaltisce condizionatori, pompe di calore e impianti di refrigerazione contenenti gas fluorurati. Riguarda sia le persone (tecnici e frigoristi) sia le imprese." }
            },
            {
              "@type": "Question",
              "name": "Dove ottenere la certificazione FGAS a Frosinone?",
              "acceptedAnswer": { "@type": "Answer", "text": "Da treffe, a Frosinone in Viale Tevere 8/A: corso di preparazione, esame teorico-pratico e supporto nelle pratiche fino all'iscrizione al Registro telematico nazionale FGAS. Info dal modulo contatti del sito o al numero 0775 1433967." }
            }
          ]
        }}
      />
      <PageHeader
        eyebrow="Certificazione FGAS · Gas fluorurati"
        title={<>Certificazione FGAS a Frosinone, per persone e imprese.</>}
        subtitle="La certificazione FGAS è obbligatoria per chi lavora su condizionatori, pompe di calore e impianti di refrigerazione con gas fluorurati. Ti seguiamo in tutto il percorso: preparazione, esame e pratiche, fino all'iscrizione al Registro telematico nazionale FGAS."
      />

      {/* Cos'è e a chi serve */}
      <section className="py-16 md:py-24 bg-white border-b border-border" data-testid="fgas-about">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-red mb-4">Cos'è e a chi serve</div>
            <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tighter text-brand-black leading-[1.02]">
              Un obbligo di legge, un percorso semplice con noi.
            </h2>
            <p className="mt-6 text-brand-ink text-base md:text-lg leading-relaxed max-w-xl">
              Chi installa, mantiene, ripara o smaltisce apparecchiature contenenti gas fluorurati deve essere certificato. Vale per il singolo tecnico e per l'impresa che opera sugli impianti: con treffe hai un unico interlocutore dalla preparazione al rilascio.
            </p>
            <ul className="mt-8 grid gap-3">
              {BULLETS.map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm md:text-base text-brand-black">
                  <span className="mt-0.5 w-5 h-5 shrink-0 bg-brand-red text-white flex items-center justify-center">
                    <Check size={13} strokeWidth={3} />
                  </span>
                  {b}
                </li>
              ))}
            </ul>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            className="bg-brand-black text-white p-7 md:p-9"
            data-testid="fgas-normativa-card"
          >
            <div className="w-12 h-12 mb-5 flex items-center justify-center bg-brand-red text-white">
              <FileText size={22} />
            </div>
            <h3 className="font-heading font-bold text-xl md:text-2xl leading-tight mb-4">Riferimenti normativi</h3>
            <ul className="space-y-3 text-sm md:text-base text-white/80 leading-relaxed">
              <li className="flex gap-3"><span className="w-2 h-2 rounded-full bg-brand-red mt-2 shrink-0" /> Regolamento (UE) 2024/573 sui gas fluorurati</li>
              <li className="flex gap-3"><span className="w-2 h-2 rounded-full bg-brand-red mt-2 shrink-0" /> D.P.R. 146/2018 di attuazione</li>
              <li className="flex gap-3"><span className="w-2 h-2 rounded-full bg-brand-red mt-2 shrink-0" /> Registro telematico nazionale FGAS: iscrizione di persone e imprese certificate</li>
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Persone / Imprese */}
      <section className="py-16 md:py-24 bg-brand-paper border-b border-border" data-testid="fgas-paths">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="max-w-3xl mb-10">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-red mb-4">Due percorsi</div>
            <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tighter text-brand-black">
              La stessa certificazione, due esigenze diverse.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {PATHS.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.article
                  key={p.title}
                  data-testid={`fgas-path-${i}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="bg-white border border-border p-6 md:p-8 hover:border-brand-red transition-colors"
                >
                  <div className="w-12 h-12 mb-5 flex items-center justify-center bg-brand-black text-white">
                    <Icon size={22} />
                  </div>
                  <h3 className="font-heading font-bold text-xl md:text-2xl text-brand-black mb-3">{p.title}</h3>
                  <p className="text-sm md:text-base text-brand-ink leading-relaxed">{p.body}</p>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Come funziona */}
      <section className="py-16 md:py-24 bg-white border-b border-border" data-testid="fgas-steps">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-red mb-4">Come funziona</div>
            <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tighter text-brand-black">
              Dal primo contatto al registro nazionale.
            </h2>
            <div className="mt-8 space-y-4">
              {STEPS.map((t, i) => (
                <motion.div
                  key={t}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, delay: i * 0.07 }}
                  className="flex gap-4"
                >
                  <span className="w-8 h-8 shrink-0 rounded-full border-2 border-brand-red text-brand-red flex items-center justify-center text-sm font-bold">{i + 1}</span>
                  <p className="text-brand-black leading-relaxed text-sm md:text-base pt-1">{t}</p>
                </motion.div>
              ))}
            </div>
          </div>
          <div className="bg-brand-black text-white p-7 md:p-9 flex flex-col justify-between gap-8">
            <div>
              <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.28em] text-brand-red mb-4">
                <Snowflake size={15} /> Parliamone
              </div>
              <p className="text-white/85 text-base md:text-lg leading-relaxed">
                Raccontaci la tua situazione — persona o impresa — compilando il form: ti rispondiamo via email con percorso, tempi e disponibilità del prossimo esame.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contatti#contatti"
                data-testid="fgas-page-form-cta"
                className="inline-flex items-center justify-center gap-2 bg-brand-red text-white text-sm font-bold uppercase tracking-widest px-8 py-4 hover:bg-white hover:text-brand-black transition-colors"
              >
                <Mail size={18} /> Compila il form e richiedi info
              </Link>
              <Link
                to="/aziende"
                data-testid="fgas-page-aziende-link"
                className="inline-flex items-center justify-center gap-2 border-2 border-white/70 text-white text-sm font-bold uppercase tracking-widest px-8 py-4 hover:bg-white hover:text-brand-black transition-colors"
              >
                Servizi per le aziende <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
